import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";

import { toast } from "react-toastify";
import dayjs from "dayjs";
import ProductsOptionTypesTemplate from "../../templates/lookups/products-option-types";
import {
  createProductOptionTypeRequest,
  deleteProductOptionTypeRequest,
  getProductOptionTypeDetailsRequest,
  getProductsOptionTypesRequest,
  updateProductDetailRequest,
} from "../../services/modules/lookups/productOptionTypes";

function ProductsOptionTypesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector(
    (state) => state.productsOptionTypes.productsOptionTypes
  );
  const count = useSelector((state) => state.productsOptionTypes.count);
  const details = useSelector(
    (state) => state.productsOptionTypes.productOptionType
  );
  const load = useSelector((state) => state.productsOptionTypes.load);

  //permissions
  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile
  );
  const permissions = useSelector(
    (state) => state.authenticate.currentUserProfile.permissions.option_types
  );
  const isSuperAdmin = currentUser?.privilege == "super_admin";

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState({
    page_size: 10,
    page_number: 1,
    keyword: "",
    sort_by: "id",
  });

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = async () => {
    dispatch(getProductOptionTypeDetailsRequest({ option_type_id: id }));
  };

  const getList = async (filterObj) => {
    const requestData = {
      params: filterObj,
    };
    dispatch(getProductsOptionTypesRequest(requestData));
  };

  // mappedData use for create custom cell to concat multi values or navigate button
  const mappedData = list?.map((data, index) => {
    return {
      ...data,
      created_at: <>{dayjs(data?.created_at).format("DD-MM-YYYY HH:mm")}</>,
      updated_at: <>{dayjs(data?.updated_at).format("DD-MM-YYYY HH:mm")}</>,
    };
  });

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: !type ? false : true,
      path: "/products-option-types",
      title: t("products_option_types.title"),
    },
    type && {
      active: false,
      title:
        type === "details"
          ? t("products_option_types.details")
          : id
          ? t("products_option_types.edit")
          : t("products_option_types.add"),
    },
  ];
  const handelSubmit = (data, formik) => {
    const action = () => {
      searchParams.delete("id");
      searchParams.delete("type");
      navigate("/products-option-types");
      getList(filterObj);
      !id && formik?.resetForm();
      // USE TOSTIFY TO CONFIRM ACTION
      toast.success(
        id ? t("products_option_types.updated") : t("products_option_types.created")
      );
    };
    const requestData = {
      body: {
        option_type: {
          image: data?.image,
          en: {
            name: data?.englishOptionType,
          },
          ar: {
            name: data?.arabicOptionType,
          },
        },
      },
      action,
    };

    if (id) {
      //update call
      dispatch(updateProductDetailRequest({ ...requestData, id }));
    } else {
      //create call
      dispatch(createProductOptionTypeRequest(requestData));
    }
  };

  const filterList = [];

  const handleDelete = (id) => {
    //action to be called after successfull deletion
    const action = () => {
      //if list.length == 1 & page !== 1 -> setPage(page-1) & fetch
      if (list.length === 1 && page !== 1) {
        setPage(page - 1);
        setFilterObj((prev) => ({
          ...prev,
          page_number: page - 1,
        }));
      } else {
        getList(filterObj);
      }
      type === "details" && navigate("/products-option-types");
      toast.success(t("products_option_types.deleted"));
    };
    const requestData = {
      id,
      action, //dispatched on success
    };
    dispatch(deleteProductOptionTypeRequest(requestData));
    setOpenDeleteModal(false);
  };

  //list of actions to be displayed when clicked in the 3 dots
  const actionsList = ({ index, data }) => {
    return [
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_update;
        },
        click: () => {
          navigate(`?type=form&id=${data?.id}`);
        },
        text: t("common.edit"),
      },
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_delete;
        },
        click: () => {
          setOpenDeleteModal(true);
        },
        text: t("common.delete"),
      },
    ];
  };

  //if you need more buttons in the header beside the add button
  const headerChildren = <></>;

  //if more modals needed, pass them to the modalComponents as below
  const modalComponents = [];

  //listing all tags
  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj]);

  //details of a single tag
  useEffect(() => {
    if (id) getDetails();
  }, [id]);

  const token = localStorage.getItem("token");
  return (
    <>
      <Loader open={load?.loading && load?.type == "list"} />
      <ProductsOptionTypesTemplate
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        data={mappedData}
        count={count}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        details={details}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        handelSubmit={handelSubmit}
        actionsList={actionsList}
        headerChildren={headerChildren}
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDeleteRequest={handleDelete}
        modalComponents={modalComponents}
        can_create={isSuperAdmin || permissions?.can_create}
      />
    </>
  );
}

export default ProductsOptionTypesPage;
